import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Text, Spacing, Icon, Button } from '@reservamos/elements';
import { useSelector } from 'react-redux';
import useProviderPhoneDisplay from 'hooks/useProviderPhoneDisplay';
import { DEFAULT_PASSENGER_CATEGORY } from '../../../constants/passengerCategories';
import passengerTypes from '../../../config/passengerTypes';
import { getDoc2BrandMap, getBrand2ProcessorMap } from '../../../utils/documentMaps';
import { normalizeGender } from '../../../utils/passengers';
import QuickPurchaseTicketSummary from '../../../ui/atoms/QuickPurchaseTicketSummary';

/**
 * QuickCheckoutTravelers component.
 * Render a unified section showing passenger cards with seat assignments
 * and edit buttons for seats and passengers.
 * @param {object} props - passengers, departureSeats, returnSeats, onEditSeats, onEditPassengers.
 * @param {Array} props.passengers - Array of passenger objects from purchase state.
 * @param {Array} props.departureSeats - Array of departure seat objects with number property.
 * @param {Array} props.returnSeats - Array of return seat objects with number property.
 * @param {Function} props.onEditSeats - Callback to edit seats (redirect or in-place, gated by flag).
 * @param {Function} props.onEditPassengers - Callback to navigate to passenger editing.
 */
const QuickCheckoutTravelers = ({
  passengers,
  departureSeats,
  returnSeats,
  onEditSeats,
  onEditPassengers,
  isEditingPassengers,
}) => {
  const { t } = useTranslation(['purchase', 'passengers', 'seats', 'general']);
  const { nationalities } = useSelector((state) => state.whitelabelConfig.env);
  const { features } = useSelector((state) => state.whitelabelConfig);
  const shouldShowPhoneField = useProviderPhoneDisplay();
  const passengerCount = passengers?.length || 0;
  const doc2BrandMap = getDoc2BrandMap();
  const brand2ProcessorMap = getBrand2ProcessorMap();
  const showGender = features.GENDER_PER_PASSENGER;

  if (!passengers || passengers.length === 0) return null;

  return (
    <div>
      <Spacing vertical size="S">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Text weight="bold" size="M">
            {t('purchase:title.quick_purchase_passengers', { count: passengerCount })}
          </Text>

          <div className="flex gap-2">
            <Button onClick={() => onEditSeats('departure')} isRounded>
              <div className="flex items-center gap-1">
                <Text size="S" weight="semibold" color="accent">
                  {t('purchase:quick_checkout.edit_seats')}
                </Text>
                <Icon type="ChevronDown" size="S" color="grayLight" />
              </div>
            </Button>

            {isEditingPassengers ? (
              <div className="rounded-full border-2 border-dashed border-[var(--accent)] p-[2px]">
                <Button onClick={onEditPassengers} isRounded>
                  <div className="flex items-center gap-1">
                    <Text size="S" weight="semibold" color="accent">
                      {t('purchase:quick_checkout.edit_passengers')}
                    </Text>
                    <Icon type="ChevronDown" size="S" color="grayLight" />
                  </div>
                </Button>
              </div>
            ) : (
              <Button onClick={onEditPassengers} isRounded>
                <div className="flex items-center gap-1">
                  <Text size="S" weight="semibold" color="accent">
                    {t('purchase:quick_checkout.edit_passengers')}
                  </Text>
                  <Icon type="ChevronDown" size="S" color="grayLight" />
                </div>
              </Button>
            )}
          </div>
        </div>

        {!isEditingPassengers && (
          <div className="flex flex-row gap-4 flex-wrap">
            {passengers.map((passenger, index) => {
              const category = passenger.busCategory || DEFAULT_PASSENGER_CATEGORY;
              const passengerType = passengerTypes[category] || passengerTypes.general;
              const seatNumber = departureSeats?.[index]?.number;
              const returnSeatNumber = returnSeats?.[index]?.number;
              const showDateOfBirth =
                features.BIRTH_DATE_PER_PASSENGER &&
                (!features.SHOW_BIRTH_DATE_EXCLUDING_ADULTS ||
                  category.toLowerCase() !== DEFAULT_PASSENGER_CATEGORY);

              const fullName = `${passenger.firstName} ${passenger.lastName}${
                passenger.secondLastName ? ` ${passenger.secondLastName}` : ''
              }`;

              let docKey = Object.entries(doc2BrandMap).find(
                ([, brand]) => brand.toLowerCase() === passenger.documentType?.toLowerCase(),
              )?.[0];

              if (!docKey) {
                docKey = Object.entries(doc2BrandMap).find(([key]) => {
                  const processor = brand2ProcessorMap[doc2BrandMap[key]];
                  return processor?.toLowerCase() === passenger.documentType?.toLowerCase();
                })?.[0];
              }

              const documentLabel = docKey
                ? t('seats:identification', { context: docKey })
                : passenger.documentType;

              const nationalityCode = passenger.nationality || passenger.isoCountryCode;
              const nationalityLabel =
                nationalities?.find((n) => n.value === nationalityCode)?.label || null;

              return (
                <QuickPurchaseTicketSummary
                  key={passenger.id || `passenger-${index}`}
                  name={fullName}
                  category={t(`passengers:${passengerType.label}`)}
                  categoryIcon={passengerType.icon}
                  documentType={documentLabel}
                  documentId={passenger.documentId}
                  seatNumber={seatNumber}
                  returnSeatNumber={returnSeatNumber}
                  email={passenger.email || null}
                  phone={shouldShowPhoneField && passenger.phone ? passenger.phone : null}
                  dateOfBirth={
                    showDateOfBirth && passenger.dateOfBirth ? passenger.dateOfBirth : null
                  }
                  gender={showGender && passenger.gender ? normalizeGender(passenger.gender) : null}
                  nationality={nationalityLabel}
                />
              );
            })}
          </div>
        )}
      </Spacing>
    </div>
  );
};

QuickCheckoutTravelers.propTypes = {
  passengers: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      secondLastName: PropTypes.string,
      busCategory: PropTypes.string,
      documentType: PropTypes.string,
      documentId: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      dateOfBirth: PropTypes.string,
      gender: PropTypes.string,
      nationality: PropTypes.string,
      isoCountryCode: PropTypes.string,
      id: PropTypes.string,
    }),
  ).isRequired,
  departureSeats: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.string,
    }),
  ),
  returnSeats: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.string,
    }),
  ),
  onEditSeats: PropTypes.func.isRequired,
  onEditPassengers: PropTypes.func.isRequired,
  isEditingPassengers: PropTypes.bool,
};

QuickCheckoutTravelers.defaultProps = {
  departureSeats: [],
  returnSeats: [],
  isEditingPassengers: false,
};

export default QuickCheckoutTravelers;
